import { SeasonPassCredentialPayload, TicketCredentialPayload } from '@bre-ticketing/contracts';

/**
 * Ticket credentials (docs/ticket-lifecycle.md §3): what goes in the QR.
 *
 *   credential = base64url(JSON payload) "." base64url(HMAC-SHA256(K_p, payloadBytes))
 *   payload    = {"v":1,"tid":"<ticket>","pid":"<performance>","cv":<credential version>}
 *   K_p        = HMAC-SHA256(master[g], "event-ticket:v1:" + performance_id)
 *
 * Authenticity is decided here; validity (refunded? superseded by a newer
 * version?) is decided by the ticket row, so a credential is deterministic
 * for (ticket, performance, version) and never needs storing. WebCrypto
 * only, so the same code runs in the API, a browser and an offline scanner.
 */

export const CREDENTIAL_KEY_INFO_PREFIX = 'event-ticket:v1:';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

export function fromBase64url(value: string): Uint8Array {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - (value.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

async function hmacKey(raw: Uint8Array, extractable = false): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, extractable, ['sign', 'verify']);
}

/**
 * The per-performance key. `master` is one generation of
 * EVENT_TICKET_SIGNING_SECRET; an offline bundle carries only this derived
 * key, never the master.
 */
export async function derivePerformanceKey(master: string, performanceId: string): Promise<CryptoKey> {
  return hmacKey(await derivePerformanceKeyBytes(master, performanceId));
}

/** The raw K_p, for an offline bundle (docs/scanner.md §5.1): this performance's key only, never the master. */
export async function derivePerformanceKeyBytes(master: string, performanceId: string): Promise<Uint8Array> {
  const masterKey = await hmacKey(encoder.encode(master));
  const derived = await crypto.subtle.sign('HMAC', masterKey, encoder.encode(`${CREDENTIAL_KEY_INFO_PREFIX}${performanceId}`));
  return new Uint8Array(derived);
}

/** A scanner imports the bundle's K_p as a non-extractable key: script can verify with it but never read it back. */
export function importPerformanceKey(raw: Uint8Array): Promise<CryptoKey> {
  return hmacKey(raw, false);
}

export interface CredentialInput {
  ticket_id: string;
  performance_id: string;
  credential_version: number;
}

export function encodePayload(input: CredentialInput): Uint8Array {
  const payload: TicketCredentialPayload = { v: 1, tid: input.ticket_id, pid: input.performance_id, cv: input.credential_version };
  return encoder.encode(JSON.stringify(payload));
}

export async function signCredential(key: CryptoKey, input: CredentialInput): Promise<string> {
  const payloadBytes = encodePayload(input);
  const signature = await crypto.subtle.sign('HMAC', key, payloadBytes as BufferSource);
  return `${base64url(payloadBytes)}.${base64url(new Uint8Array(signature))}`;
}

export type CredentialParse = { ok: true; payload: TicketCredentialPayload; payloadBytes: Uint8Array; signature: Uint8Array } | { ok: false; reason: 'MALFORMED' | 'BAD_PAYLOAD' };

/** Splits and validates the shape without checking the signature (the key depends on the payload's performance). */
export function parseCredential(credential: string): CredentialParse {
  const parts = credential.trim().split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return { ok: false, reason: 'MALFORMED' };
  let payloadBytes: Uint8Array;
  let signature: Uint8Array;
  try {
    payloadBytes = fromBase64url(parts[0]);
    signature = fromBase64url(parts[1]);
  } catch {
    return { ok: false, reason: 'MALFORMED' };
  }
  let json: unknown;
  try {
    json = JSON.parse(decoder.decode(payloadBytes));
  } catch {
    return { ok: false, reason: 'BAD_PAYLOAD' };
  }
  const parsed = TicketCredentialPayload.safeParse(json);
  if (!parsed.success) return { ok: false, reason: 'BAD_PAYLOAD' };
  return { ok: true, payload: parsed.data, payloadBytes, signature };
}

/** True when the signature was minted with this key over exactly these bytes. */
export async function verifyCredential(key: CryptoKey, parsed: Extract<CredentialParse, { ok: true }>): Promise<boolean> {
  return crypto.subtle.verify('HMAC', key, parsed.signature as BufferSource, parsed.payloadBytes as BufferSource);
}

// --- Season passes (D-373) ----------------------------------------------------------------
//
//   credential = base64url(JSON payload) "." base64url(HMAC-SHA256(K_s, payloadBytes))
//   payload    = {"v":1,"typ":"SP","sp":"<pass>","sid":"<season>","cv":<credential version>}
//   K_s        = HMAC-SHA256(master[g], "season-pass:v1:" + season_id)
//
// Its own key purpose: a performance key can never verify a pass and a season
// key can never verify a ticket, because the derivation labels differ. Per
// season rather than per performance, because one pass admits to every game;
// an offline bundle carries only the key of the season tonight's game is in.
// Validity (suspended? reissued?) is the pass row's, exactly as for a ticket.

export const SEASON_PASS_KEY_INFO_PREFIX = 'season-pass:v1:';

/** The raw K_s for one season, for an offline bundle: never the master. */
export async function deriveSeasonPassKeyBytes(master: string, seasonId: string): Promise<Uint8Array> {
  const masterKey = await hmacKey(encoder.encode(master));
  const derived = await crypto.subtle.sign('HMAC', masterKey, encoder.encode(`${SEASON_PASS_KEY_INFO_PREFIX}${seasonId}`));
  return new Uint8Array(derived);
}

export async function deriveSeasonPassKey(master: string, seasonId: string): Promise<CryptoKey> {
  return hmacKey(await deriveSeasonPassKeyBytes(master, seasonId));
}

/** A scanner imports K_s as a non-extractable key, like K_p. */
export function importSeasonPassKey(raw: Uint8Array): Promise<CryptoKey> {
  return hmacKey(raw, false);
}

export interface PassCredentialInput {
  pass_id: string;
  season_id: string;
  credential_version: number;
}

export function encodePassPayload(input: PassCredentialInput): Uint8Array {
  const payload: SeasonPassCredentialPayload = { v: 1, typ: 'SP', sp: input.pass_id, sid: input.season_id, cv: input.credential_version };
  return encoder.encode(JSON.stringify(payload));
}

export async function signPassCredential(key: CryptoKey, input: PassCredentialInput): Promise<string> {
  const payloadBytes = encodePassPayload(input);
  const signature = await crypto.subtle.sign('HMAC', key, payloadBytes as BufferSource);
  return `${base64url(payloadBytes)}.${base64url(new Uint8Array(signature))}`;
}

export type PassCredentialParse = { ok: true; payload: SeasonPassCredentialPayload; payloadBytes: Uint8Array; signature: Uint8Array } | { ok: false; reason: 'MALFORMED' | 'BAD_PAYLOAD' };

/** Splits and validates a pass credential's shape without checking the signature. */
export function parsePassCredential(credential: string): PassCredentialParse {
  const parts = credential.trim().split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return { ok: false, reason: 'MALFORMED' };
  let payloadBytes: Uint8Array;
  let signature: Uint8Array;
  try {
    payloadBytes = fromBase64url(parts[0]);
    signature = fromBase64url(parts[1]);
  } catch {
    return { ok: false, reason: 'MALFORMED' };
  }
  let json: unknown;
  try {
    json = JSON.parse(decoder.decode(payloadBytes));
  } catch {
    return { ok: false, reason: 'BAD_PAYLOAD' };
  }
  const parsed = SeasonPassCredentialPayload.safeParse(json);
  if (!parsed.success) return { ok: false, reason: 'BAD_PAYLOAD' };
  return { ok: true, payload: parsed.data, payloadBytes, signature };
}

/** True when the pass credential was signed with this season key over exactly these bytes. */
export async function verifyPassCredential(key: CryptoKey, parsed: Extract<PassCredentialParse, { ok: true }>): Promise<boolean> {
  return crypto.subtle.verify('HMAC', key, parsed.signature as BufferSource, parsed.payloadBytes as BufferSource);
}

/**
 * What a scanned string is: a ticket credential, a season pass credential, or
 * neither. Decided by the payload's shape, so a scanner reaches for the right
 * key the first time.
 */
export function credentialKind(raw: string): 'TICKET' | 'SEASON_PASS' | 'UNREADABLE' {
  if (parseCredential(raw).ok) return 'TICKET';
  if (parsePassCredential(raw).ok) return 'SEASON_PASS';
  return 'UNREADABLE';
}
